import { coordToArr } from "@nova/calculate-geospatial"
import { Perimeter } from "@nova/protobuf"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { createPerimeter } from "features/pubsub/entityActions/cubes/perimeter"
import store from "integrations/redux/store"
import { selectUserID } from "user/profile/selectors"
import { createDuplicatedEntityName } from "../module"
import { decideTakeSnapshotOfSecondaryLayer } from "features/map/control/module"
import { ColorSwatchCategory, hexColorSwatches } from "@nova/constants"
import { encodeColorToNumber } from "@nova/serializer/src/client/util"

export const zoomToFirePerimeter = (entity: Perimeter) => {
    decideTakeSnapshotOfSecondaryLayer()
    store.dispatch(
        zoomToShowPoints({
            points: entity.polygons.flatMap((e) =>
                e.coordinates.map((e) => coordToArr(e) as [number, number])
            ),
        })
    )
}

export const duplicatePerimeter = (entity: Perimeter) => {
    const userId = selectUserID(store.getState())
    if (!userId) return

    store.dispatch(
        createPerimeter({
            data: {
                polygons: entity.polygons,
                buffers: entity.buffers,
                outline: entity.outline,
                stroke: entity.stroke,
                width: entity.width,
                name: createDuplicatedEntityName(entity.name),
                parentId: entity.parentId,
                createdBy: { userId },
            },
        })
    )
}

export const encodedFirePerimeterColorSwatches = hexColorSwatches[
    ColorSwatchCategory.FirePerimeterBuffer
].map((hex) => encodeColorToNumber(hex))

/**
 * Determines the next buffer color based on the previous one.
 *
 * If the previous buffer color is one from the default swatches, then the next buffer color
 * will be the next swatch (or the first swatch if the previous buffer color is the last
 * swatch). If the previous buffer color is a custom value, then the next buffer color will
 * be the swatch at index: buffers.length, effectively the "next swatch in terms of array
 * position".
 *
 * This logic mitigates duplicated buffer colors when buffer's get added, deleted, then added.
 */
export const nextBufferColor = (existingBuffers: { color: number }[] | undefined): number => {
    const lastBuffer = existingBuffers?.at(-1)
    if (!existingBuffers || !lastBuffer) return encodedFirePerimeterColorSwatches[0]

    const swatchIndexOfLastBufferColor = encodedFirePerimeterColorSwatches.findLastIndex(
        (color) => color === lastBuffer.color
    )

    const nextSwatchIndex =
        (swatchIndexOfLastBufferColor === -1
            ? existingBuffers.length
            : swatchIndexOfLastBufferColor + 1) % encodedFirePerimeterColorSwatches.length

    return encodedFirePerimeterColorSwatches[nextSwatchIndex]
}
